import { Stack } from '@mui/material';
import { type FlowGetNotableObjectBlock, FlowNotableObject } from 'flow-schema';
import { type ComponentType } from 'react';

import { isDefined } from 'common';

import { AgentConversationFormFields } from './FormFields/agentConversationFormFields';
import { AssistantFormFields } from './FormFields/assistantFormFields';
import { EncounterContactsFormFields } from './FormFields/encounterContactsFormFields';
import { EncounterFormFields } from './FormFields/encountersFormFields';
import { ExternalDocumentPacketsFormFields } from './FormFields/externalDocumentPackets';
import { FeedMessagesFormFields } from './FormFields/feedMessagesFormFields';
import { GuarantorFormFields } from './FormFields/guarantorsFormFields';
import { Hl7MessageEventFormFields } from './FormFields/hl7MessageFormFields';
import { InsuranceOcrResultsFormFields } from './FormFields/insuranceOcrResultsFormFields';
import { JobContextFormFields } from './FormFields/jobContextFormFields';
import { PatientFormFields } from './FormFields/patientFormFields';
import { PatientInsuranceInformationFormFields } from './FormFields/patientInsuranceInformationFormFields';
import { PatientOrderFormFields } from './FormFields/patientOrderFormFields';
import { PatientUploadFormFields } from './FormFields/patientUploadFormFields';
import { PaymentFormFields } from './FormFields/paymentFormFields';
import { PracticeFormFields } from './FormFields/practiceFormFields';
import { PrimaryCareProviderFormFields } from './FormFields/primaryCareProviderFormFields';
import { ProviderFormFields } from './FormFields/providerFormFields';
import { ReferenceTableFormFields } from './FormFields/referenceTableFormFields';
import { SftpMessageEventFormFields } from './FormFields/sftpMessageFormFields';
import { TemplateFormFields } from './FormFields/templateFormFields';
import { X12MessageEventFormFields } from './FormFields/x12MessageFormFields';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const GET_NOTABLE_OBJECT_FORM_FIELDS: Partial<Record<FlowNotableObject, ComponentType>> = {
  [FlowNotableObject.AgentConversation]: AgentConversationFormFields,
  [FlowNotableObject.Guarantor]: GuarantorFormFields,
  [FlowNotableObject.Payment]: PaymentFormFields,
  [FlowNotableObject.Template]: TemplateFormFields,
  [FlowNotableObject.Hl7MessageEvent]: Hl7MessageEventFormFields,
  [FlowNotableObject.ReferenceTable]: ReferenceTableFormFields,
  [FlowNotableObject.PatientOrder]: PatientOrderFormFields,
  [FlowNotableObject.Provider]: ProviderFormFields,
  [FlowNotableObject.ExternalDocumentPacket]: ExternalDocumentPacketsFormFields,
  [FlowNotableObject.PatientInsuranceInformation]: PatientInsuranceInformationFormFields,
  [FlowNotableObject.Patient]: PatientFormFields,
  [FlowNotableObject.Practice]: PracticeFormFields,
  [FlowNotableObject.SftpMessageEvent]: SftpMessageEventFormFields,
  [FlowNotableObject.X12MessageEvent]: X12MessageEventFormFields,
  [FlowNotableObject.PatientUpload]: PatientUploadFormFields,
  [FlowNotableObject.Encounter]: EncounterFormFields,
  [FlowNotableObject.PrimaryCareProvider]: PrimaryCareProviderFormFields,
  [FlowNotableObject.FeedMessages]: FeedMessagesFormFields,
  [FlowNotableObject.EncounterContacts]: EncounterContactsFormFields,
  [FlowNotableObject.InsuranceOcrResults]: InsuranceOcrResultsFormFields,
  [FlowNotableObject.JobContext]: JobContextFormFields,
  [FlowNotableObject.Assistant]: AssistantFormFields,
};

export function GetNotableObjectFormFields() {
  const { value: notableObject } =
    useSelectedNodeField<FlowGetNotableObjectBlock['config']['notableObject']>(
      'config.notableObject'
    );
  const excludedObjects = [
    FlowNotableObject.ServiceReviewSubmissions,
    FlowNotableObject.AuthorizationSubmissions,
  ];
  const options = Object.values(FlowNotableObject)
    .filter((notableObject) => !excludedObjects.includes(notableObject))
    .map((object) => ({
      label: object,
      value: object,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const FormFields = isDefined(notableObject)
    ? GET_NOTABLE_OBJECT_FORM_FIELDS[notableObject]
    : undefined;
  return (
    <>
      <BlockSelectField
        label="Notable Object"
        fieldName="config.notableObject"
        required
        options={options}
      />
      {isDefined(FormFields) && (
        <Stack gap={3}>
          {FormFields && (
            <>
              <FormFields />
            </>
          )}
        </Stack>
      )}
    </>
  );
}
